/**
 * Pure helpers for usage tracking (spec: docs/superpowers/specs/2026-10-03-usage-tracking-design.md).
 * What a tap is called in the log, and which tap a screen change is credited to.
 * The buffering and writing live in services/usageLog.ts.
 */

/** Longest tap name kept — enough to tell buttons apart, short enough that a job card's address isn't saved whole. */
export const TAP_TARGET_MAX = 40;
/** A screen change this soon after a tap is credited to that tap; later than this it came from somewhere else (back gesture, redirect). */
export const VIA_WINDOW_MS = 1500;
/** Buffer size that triggers an early write. */
export const FLUSH_AT = 50;
/** Most events per written batch — firestore.rules enforces the same cap. */
export const MAX_BATCH = 200;

export type UsageEvent =
  | { t: number; type: 'open'; screen: string; bottomTabs: string[]; tabsCustomized: boolean; standalone: boolean; wide: boolean }
  | { t: number; type: 'resume'; screen: string }
  | { t: number; type: 'tap'; screen: string; target: string }
  | { t: number; type: 'screen'; screen: string; from: string; via: string };

const TAPPABLE = 'button, a, [role="button"], [data-track]';
// Somewhere the person types. A tap there is never logged, so typed text can't leak in.
const TYPING = 'textarea, select, [contenteditable=""], [contenteditable="true"], input:not([type="button"]):not([type="submit"]):not([type="reset"])';

const clip = (s: string): string => s.replace(/\s+/g, ' ').trim().slice(0, TAP_TARGET_MAX);

/**
 * The name to log for a tap, or null when it wasn't on a button/link at all.
 * Order: the element's stable `data-track` id, then its aria-label, then its visible text,
 * then its hover title. A wordless picture is "photo" — its alt is often a file name, never logged.
 */
export const describeTapTarget = (start: EventTarget | null): string | null => {
  if (!(start instanceof Element)) return null;
  if (start.closest(TYPING)) return null;
  const tracked = start.closest('[data-track]');
  if (tracked) return clip(tracked.getAttribute('data-track') ?? '') || '?';
  const el = start.closest(TAPPABLE);
  if (!el) return null;
  return clip(el.getAttribute('aria-label') ?? '')
    || clip(el.textContent ?? '')
    || clip(el.getAttribute('title') ?? '')
    || (el.querySelector('img') ? 'photo' : '?');
};

export const viaFor = (lastTap: { target: string; t: number } | null, now: number): string =>
  lastTap && now - lastTap.t <= VIA_WINDOW_MS ? lastTap.target : 'other';
